import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useContextSelector } from 'use-context-selector'

import Button, { IconButton } from 'components/uiKit/Button'
import { KitSize } from 'components/uiKit/KitTypes'
import type { TemplatesListSchemaFragment } from 'gql/__generated__/graphql'
import { EmployeePreferencesTab } from 'gql/__generated__/graphql'
import { t } from 'services/Translation'

import type { ITemplatePickerContext } from '../hooks'
import { TemplatePickerContext, useLoadAllIds } from '../hooks'
import { TemplatePickerMode } from '../types'
import * as s from './TemplatePickerFooter.module.scss'

interface ISectionPickerProps {
  onSubmit: (result: { selected: string[]; template?: TemplatesListSchemaFragment }) => void
  clearSelect: () => void
  setSelected: Dispatch<SetStateAction<ITemplatePickerContext['selected']>>
  templates: TemplatesListSchemaFragment[]
  tab: EmployeePreferencesTab
  hideAddAll?: boolean
  disabled?: boolean
}

const TemplatePickerFooter: React.FC<ISectionPickerProps> = ({
  onSubmit,
  templates,
  clearSelect,
  setSelected,
  tab,
  hideAddAll,
  disabled,
}) => {
  const {
    mode,
    selected,
    step,
    setStep,
    collection,
    allSelected,
    blocksByMode,
    search,
    tag,
    companyId,
    sectionType,
    projectId,
    blockMode,
  } = useContextSelector(TemplatePickerContext, (value) => value)

  const [allIdsLoading, setAllIdsLoading] = useState(false)

  const vars = useMemo(
    () => ({
      search,
      tag,
      editorTemplateGroupId: collection?.id,
      companyId,
      sectionType,
      projectId,
      tab,
      blockMode,
    }),
    [search, tag, collection, companyId, sectionType, projectId, tab, blockMode],
  )

  const loadAllIds = useLoadAllIds(vars)
  const selectAll = async () => {
    setAllIdsLoading(true)
    const data = await loadAllIds()
    const templatesAllIds = data?.data.data || []
    setSelected((prev) => ({ ...prev, multi: templatesAllIds }))
    setAllIdsLoading(false)
  }

  const nextStep = useCallback(
    () => setStep((prev) => (prev !== null ? prev + 1 : null)),
    [setStep],
  )

  const tabHasStep =
    (tab === EmployeePreferencesTab.collections && !!collection?.id) ||
    tab !== EmployeePreferencesTab.collections

  const next = (step === 0 || step === 1) && mode !== TemplatePickerMode.change && tabHasStep

  const onClick = useCallback(() => {
    const template = templates.find((template) => template.id === allSelected[0])
    onSubmit({ template, selected: allSelected })
  }, [allSelected, onSubmit, templates])

  return (
    <div className={s.modalFooter}>
      <div className={s.leftSide}>
        {mode === TemplatePickerMode.multi && !hideAddAll && (
          <Button
            disabled={!templates.length || Boolean(selected.multi.length) || disabled}
            loading={allIdsLoading}
            name='selectAll'
            onClick={selectAll}
            styleType='ghost'
          >
            {t('uiKit.button.chooseAll')}
          </Button>
        )}
        <div className={s.close}>
          {Boolean(selected.multi.length) && (
            <div className={s.actionsContainer}>
              {mode === TemplatePickerMode.multi && (
                <div className={s.selected}>
                  <IconButton
                    icon='otherClose'
                    name='close'
                    onClick={clearSelect}
                    size={KitSize.S}
                    styleType='ghost'
                  />
                  <span className={s.selectedTitle}>
                    {t('modal.pickerTemplate.templates.selected')}
                    {selected.multi.length}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={s.buttons}>
        <Button
          disabled={!allSelected.length}
          name={mode === TemplatePickerMode.change ? 'change' : 'add'}
          onClick={onClick}
          styleType={next && !blocksByMode?.end ? 'secondary' : 'primary'}
        >
          {mode === TemplatePickerMode.change ? t('uiKit.button.replace') : t('uiKit.button.add')}
        </Button>
        {next && !blocksByMode?.end && (
          <Button name='next' onClick={nextStep}>
            {t('uiKit.button.next')}
          </Button>
        )}
      </div>
    </div>
  )
}

export default TemplatePickerFooter
